import { NodeType, Node as PmNode } from 'prosemirror-model'

import type { ScriptFormat } from '@showrunner/codex'
import { constants, schema } from '@showrunner/prose-schemas'

import { MoresContds } from '#util/formats'

import { linesAfter, linesBetween } from './blockLines'
import {
  DefaultFlowPaginationPolicy,
  PageBreakType,
  PaginationAction,
  PaginationBlockInfo,
  PaginationContext,
  PaginationPolicy,
} from './Paginator'
import { BlockGroups, canSplitInline, InlineSplitPoints } from './split-points'
const { NodeTypeMap } = constants

// Policy to start a new page when a block has an explicit manual break marker.
export class ManualPageBreakPolicy extends PaginationPolicy {
  override decide(ctx: PaginationContext): PaginationAction | null {
    if (!ctx.block.manualBreak) return null
    return ctx.page.used > 0
      ? { kind: 'break-before', breakType: PageBreakType.ManualBreak }
      : null
  }
}

// Block types that always start a new page in screenplays. Hardcoded for now;
// will move to a per-block doc attr later.
const START_NEW_PAGE_BLOCK_TYPES: NodeType[] = [schema.nodes.new_act]

// Policy to start a new page before any block whose type is in
// START_NEW_PAGE_BLOCK_TYPES. Applies only to screenplays for now.
class StartNewPagePolicy extends PaginationPolicy {
  constructor(private readonly scriptType: ScriptFormat) {
    super()
  }

  override decide(ctx: PaginationContext): PaginationAction | null {
    if (this.scriptType !== 'screenplay') return null
    if (!START_NEW_PAGE_BLOCK_TYPES.includes(ctx.block.node.type)) {
      return null
    }
    return ctx.page.used > 0 ? { kind: 'break-before' } : null
  }
}

// Blocks that should be kept with the following blocks
// (e.g. a character block should not be the last line on a page, separated from their dialogue).
export class KeepWithNextPolicy extends PaginationPolicy {
  constructor(
    private readonly moreContd: MoresContds,
    private readonly blockGroups: BlockGroups,
    private readonly inlineSplitPoints: InlineSplitPoints,
  ) {
    super()
  }

  override decide(ctx: PaginationContext): PaginationAction | null {
    const group = this.blockGroups.findGroup(ctx.block.pos)
    if (!group || ctx.block.pos !== group.from || ctx.page.used === 0) {
      return null
    }

    let linesBeforeOverflow = ctx.block.spaceAbove + ctx.block.contentHeight
    let overflowingBlock: PaginationBlockInfo | null = null
    let offset = 1

    for (;;) {
      const next = ctx.peek(offset)
      if (!next || next.pos >= group.to) break

      if (
        linesBeforeOverflow + next.spaceAbove + next.contentHeight >
        ctx.page.remaining
      ) {
        overflowingBlock = next
        break
      }

      linesBeforeOverflow += next.spaceAbove + next.contentHeight
      offset++
    }

    if (!overflowingBlock) return null

    // find the first split point in the overflowing block and check
    // if splitting there would keep part before the split on the current page
    // Actual page splitting is deferred to the downstream split policies;
    // this just checks if splitting would allow keeping some content on the current page

    if (!isSplittableBlock(this.moreContd, overflowingBlock.node)) {
      return { kind: 'break-before' }
    }

    const available =
      ctx.page.remaining - linesBeforeOverflow - overflowingBlock.spaceAbove
    return findInlineSplit(
      overflowingBlock,
      overflowingBlock.pos,
      available,
      this.inlineSplitPoints,
    )
      ? { kind: 'place-block' }
      : { kind: 'break-before' }
  }
}

// Split eligible blocks at sentence boundaries; dialogue and parentheticals get continuation markers.
export class InlineSplitPolicy extends PaginationPolicy {
  constructor(
    private readonly moreContd: MoresContds,
    private readonly inlineSplitPoints: InlineSplitPoints,
  ) {
    super()
  }

  override decide(ctx: PaginationContext): PaginationAction | null {
    if (!isSplittableBlock(this.moreContd, ctx.block.node)) return null
    return splitTextBlock(
      ctx,
      this.inlineSplitPoints,
      ctx.block.node.type.name === NodeTypeMap.DIALOGUE ||
        ctx.block.node.type.name === NodeTypeMap.PARENTHETICAL
        ? PageBreakType.DialogueContinuation
        : PageBreakType.Normal,
    )
  }
}

// Split a block at the last inline split point that fits on the current page.
function splitTextBlock(
  ctx: PaginationContext,
  inlineSplitPoints: InlineSplitPoints,
  breakType: PageBreakType.DialogueContinuation | PageBreakType.Normal,
): PaginationAction | null {
  if (!ctx.block.overflows) return null

  const availableLinesInPage =
    ctx.page.used === 0
      ? ctx.page.remaining
      : ctx.page.remaining - ctx.block.spaceAbove

  const split = findInlineSplit(
    ctx.block,
    ctx.block.resumePos,
    availableLinesInPage,
    inlineSplitPoints,
  )
  return split ? { kind: 'split-and-break', breakType, ...split } : null
}

const MIN_CURRENT_PAGE_LINES = 2
const MIN_CONTINUATION_LINES = 2

/**
 * Finds the latest sentence boundary that fits while leaving enough block
 * content on both pages. MORE/CONTD space is reserved by page decoration and
 * is intentionally excluded from these calculations.
 */
function findInlineSplit(
  block: Pick<
    PaginationBlockInfo,
    'node' | 'pos' | 'sections' | 'contentHeight'
  >,
  fromPos: number,
  availableLines: number,
  inlineSplitPoints: InlineSplitPoints,
): { pos: number; linesBefore: number } | null {
  const { node, pos, sections, contentHeight } = block
  const totalLines = sections.reduce((n, s) => n + s.length, 0)
  if (totalLines === 0) return null

  const lineSpanPerWrappedLine = contentHeight / totalLines
  const minLinesBefore = MIN_CURRENT_PAGE_LINES * lineSpanPerWrappedLine
  const minLinesAfter = MIN_CONTINUATION_LINES * lineSpanPerWrappedLine
  const start = contentOffset(node, pos, fromPos)

  const splitPoints = inlineSplitPoints.filter(fromPos, pos + node.nodeSize)
  // Search backwards to keep as much content as possible on the current page.
  for (let i = splitPoints.length - 1; i >= 0; i--) {
    const splitPos = splitPoints[i].to
    if (splitPos <= fromPos) continue

    const splitOffset = contentOffset(node, pos, splitPos)
    const linesBefore = linesBetween(
      start,
      splitOffset,
      sections,
      lineSpanPerWrappedLine,
    )
    const linesAfterSplit = linesAfter(
      splitOffset,
      sections,
      lineSpanPerWrappedLine,
    )

    if (
      linesBefore >= minLinesBefore &&
      linesBefore <= availableLines &&
      linesAfterSplit >= minLinesAfter
    ) {
      return { pos: splitPos, linesBefore }
    }
  }

  return null
}

function isSplittableBlock(moreContd: MoresContds, node: PmNode): boolean {
  const dialogueContinuationsEnabled =
    moreContd?.enableDialogueMores === true &&
    moreContd?.enableDialogueContds === true
  return canSplitInline(node, dialogueContinuationsEnabled)
}

function contentOffset(node: PmNode, blockPos: number, pos: number): number {
  return Math.max(0, Math.min(node.content.size, pos - blockPos - 1))
}

export class AggregatePaginationPolicy extends PaginationPolicy {
  constructor(private policies: PaginationPolicy[]) {
    super()
  }

  override decide(ctx: PaginationContext): PaginationAction | null {
    for (const policy of this.policies) {
      const result = policy.decide(ctx)
      if (result !== null) return result
    }
    return null
  }
}

interface PaginationPolicyOptions {
  moreContd: MoresContds
  blockSplitPoints: BlockGroups
  inlineSplitPoints: InlineSplitPoints
  scriptType?: ScriptFormat
}

// Factory for creating pagination policies based on the script context.
export class PaginationPolicyFactory {
  static create(options: PaginationPolicyOptions): PaginationPolicy {
    const {
      moreContd,
      blockSplitPoints,
      inlineSplitPoints,
      scriptType = 'screenplay',
    } = options
    return new AggregatePaginationPolicy([
      new ManualPageBreakPolicy(),
      new StartNewPagePolicy(scriptType),
      new KeepWithNextPolicy(moreContd, blockSplitPoints, inlineSplitPoints),
      new InlineSplitPolicy(moreContd, inlineSplitPoints),
      DefaultFlowPaginationPolicy.instance,
    ])
  }
}
